import { useEffect, useState } from 'react';
import { ICON_BUTTON_SIZE } from '../components/touch-target';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowPathRoundedSquareIcon,
  ArrowsPointingInIcon,
  Bars3Icon,
  CheckBadgeIcon,
  CheckCircleIcon,
  CheckIcon,
  CursorArrowRaysIcon,
  EllipsisHorizontalIcon,
  ExclamationTriangleIcon,
  FolderArrowDownIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PlayIcon,
  SparklesIcon,
  Squares2X2Icon,
  StarIcon,
  TagIcon,
  TrashIcon,
  WrenchScrewdriverIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import type { AssetSort, SavedFilter, SortDir } from '@xphoto/shared';
import { useI18n } from '../i18n';
import { Menu, type MenuOption } from '../components/Menu';
import { ShareButton } from '../components/ShareButton';
import type { ShareTarget } from '../share';
import { recordAction } from '../diagnostics/buffers';
import { ApiError, enqueueScan } from './api';
import { LIBRARY_ID, DENSITY_ORDER, type Density } from './config';
import { interpolate } from './format';
import { OverflowMenu } from './OverflowMenu';
import { SavedFiltersMenu } from './SavedFiltersMenu';
import { SortMenu } from './SortMenu';
import type { MediaTypeFilter } from './state';
import { ToolsMenu, type ToolAction } from './ToolsMenu';

/**
 * Barre d'outils de la galerie (XPHOTO-20) : compteur, bouton « Recherche » (UNIQUE
 * point d'entrée — nom de fichier + note + type + tags, dans le panneau `AdvancedSearchModal`),
 * menu de tri CONSOLIDÉ (critère + ordre + filtre Type), densité (S/M/L), menu Outils
 * (scan de masse). Chaque contrôle modifie l'état de requête ; les filtres se répercutent
 * en params d'URL API. Responsive : les contrôles passent à la ligne sur petit écran.
 *
 * Elle porte aussi l'ENTRÉE DU MODE SÉLECTION (v1.41.0) : un bouton bascule
 * « Sélectionner » / « Terminé » dans la rangée de comptage. Tout ce qui touche à la
 * sélection — « Tout sélectionner (N) », le compteur, « Déplacer… », « Corbeille »,
 * « Actions… » — est RÉSERVÉ à ce mode ; hors mode, la barre reste celle de la simple
 * consultation.
 */
interface ToolbarProps {
  total: number;
  selectedCount: number;
  /**
   * Ce que le bouton « Partager » proposera : la SÉLECTION si elle est non vide,
   * sinon le DOSSIER courant (récursif ; `''` = toute la bibliothèque visible).
   * Calculé par `GalleryView`, qui seul connaît la sélection et le dossier actif.
   */
  shareTarget: ShareTarget;
  /** Titre proposé dans la fenêtre de partage (nom du dossier, ou compte de médias). */
  shareTitle: string;
  onClearSelection: () => void;
  /**
   * MODE SÉLECTION actif (v1.41.0) — `mode explicite || sélection non vide`, résolu par
   * `GalleryView`. C'est LUI qui commande l'affichage des cases sur les tuiles et de la
   * barre de sélection ici. Remplace le mode IMPLICITE de la v1.35.0, où la case était
   * visible en permanence au tactile (grille illisible sur téléphone).
   */
  selectionMode: boolean;
  /**
   * Bascule du bouton « Sélectionner » / « Terminé ». Sortir du mode VIDE la sélection
   * (jamais de sélection non vide invisible), c'est `GalleryView` qui l'applique.
   */
  onToggleSelectionMode: () => void;
  /**
   * La sélection (et donc le bouton « Sélectionner ») est-elle disponible dans la vue
   * courante ? Faux dans la vue « cartes de dossiers » (aucune image à sélectionner).
   */
  selectionAvailable: boolean;
  /**
   * « Tout sélectionner » : coche TOUT le groupe courant (mêmes filtres que la grille),
   * pas seulement les tuiles chargées. Résolution côté serveur (peut porter sur des
   * milliers d'assets).
   */
  onSelectAll: () => void;
  /** Appel « Tout sélectionner » en cours (désactive le bouton). */
  selectAllBusy: boolean;
  /** Message d'erreur du « Tout sélectionner » (non bloquant), sinon `undefined`. */
  selectAllError: string | undefined;
  sort: AssetSort;
  onSortChange: (value: AssetSort) => void;
  dir: SortDir;
  onDirChange: (value: SortDir) => void;
  /** Génère une nouvelle graine d'aléatoire (bouton « Re-mélanger » du menu Trier & filtrer). */
  onReshuffle: () => void;
  /** Un « Re-mélanger » est en vol (bouton désactivé — garde de double-soumission). */
  reshuffleBusy: boolean;
  /** Message d'échec du dernier « Re-mélanger », ou `undefined`. */
  reshuffleError: string | undefined;
  /** Filtre par type de média (`null` = tous) — section « Type » du menu Trier & filtrer. */
  mediaType: MediaTypeFilter | null;
  onMediaTypeChange: (value: MediaTypeFilter | null) => void;
  /** Nombre d'images STATIQUES présentes (compteur `byKind` de la section Type). */
  imageCount: number;
  /** Nombre d'images ANIMÉES présentes (compteur `byKind` de la section Type). */
  animatedImageCount: number;
  /** Nombre de vidéos présentes (compteur `byKind` affiché dans la section Type). */
  videoCount: number;
  /** Bascule « Non taguées » active (filtre `tagged=false` du système « Taguer »). */
  untaggedOnly: boolean;
  /** Active/désactive le filtre « Non taguées ». */
  onUntaggedOnlyChange: (value: boolean) => void;
  /** Bascule « À valider (IA) » active (propositions IA en attente, non validées). */
  aiReviewOnly: boolean;
  /** Active/désactive le filtre « À valider (IA) ». */
  onAiReviewOnlyChange: (value: boolean) => void;
  // --- Filtres enregistrés + expression booléenne (menu « Mes filtres »). ---
  /** Filtres enregistrés (triés par position). */
  savedFilters: SavedFilter[];
  /** Chargement de la liste des filtres enregistrés en cours. */
  savedFiltersLoading: boolean;
  /** Message d'erreur de chargement des filtres enregistrés, sinon `undefined`. */
  savedFiltersError: string | undefined;
  /** Expression avancée courante (`state.expr`) — surligne le filtre correspondant. */
  activeExpr: string | undefined;
  /** Applique l'`expr` d'un filtre enregistré (état propre). */
  onApplySavedFilter: (expr: string) => void;
  /** Ouvre la saisie « Enregistrer le filtre actuel… ». */
  onSaveCurrentFilter: () => void;
  /** Ouvre l'éditeur d'expression avancée. */
  onEditExpr: () => void;
  /** Ouvre l'écran « Gérer mes filtres… ». */
  onManageFilters: () => void;
  /** Ouvre le panneau « Recherche » (nom de fichier / note min / types / tags). */
  onOpenAdvancedSearch: () => void;
  /**
   * Au moins un critère de recherche est actif (nom de fichier / note / type / tags)
   * → surligne le déclencheur « Recherche ».
   */
  advancedActive: boolean;
  density: Density;
  onDensityChange: (value: Density) => void;
  /**
   * Nombre de vidéos avec piste audio (compteur `withAudio`) — affiché à côté de
   * l'option « Vidéos avec son » du menu Trier & filtrer.
   */
  withAudio: number;
  /** Ouvre la modale « déplacer toutes les vidéos avec son » (LOT 2). */
  onMoveWithAudio: () => void;
  /** Ouvre la modale « déplacer la sélection » — bouton DIRECT de la barre (XPHOTO-32). */
  onMoveSelection: () => void;
  /** Ouvre la modale « compresser la sélection » (compression SANS PERTE, images JPEG/PNG). */
  onCompressSelection: () => void;
  /** Ouvre la modale « pivoter la sélection » (rotation par le tag EXIF Orientation). */
  onRotateSelection: () => void;
  /** Ouvre la modale « noter la sélection » (0–5 étoiles, LOT 3). */
  onRateSelection: () => void;
  /** Ouvre la modale « ajouter un tag » (tags EXISTANTS, LOT 4). */
  onAddTagSelection: () => void;
  /** Ouvre le TAGUEUR par photo sur la sélection (« Réviser / valider les tags »). */
  onTagSelection: () => void;
  /** Ouvre la visionneuse en DIAPORAMA (lecture auto) sur la sélection. */
  onSlideshowSelection: () => void;
  /** Accepte en masse les suggestions IA (`proposed`→`validated`) de la sélection. */
  onAcceptAiTags: () => void;
  /**
   * Ouvre la confirmation « mettre la sélection à la poubelle » (corbeille réversible,
   * Lot 1) — bouton DIRECT de la barre depuis XPHOTO-32 lot 2.
   */
  onDeleteSelection: () => void;
  /** Télécharge les originaux de la sélection en une archive ZIP. */
  onDownloadSelection: () => void;
  /** Préparation/téléchargement de l'archive ZIP en cours (désactive le bouton). */
  downloadBusy: boolean;
  /** Message d'erreur du téléchargement (non bloquant), sinon `undefined`. */
  downloadError: string | undefined;
  /** Appelé après un scan enfilé avec succès (rafraîchit facettes/compteurs). */
  onScanQueued: () => void;
  /**
   * Un job `scan` est ACTIF pour la bibliothèque (dérivé de l'activité des jobs,
   * partagée avec la cloche) → verrouille le lancement d'un second scan.
   */
  scanActive: boolean;
  /** Ouvre le NETTOYAGE EXIF (effacement, PAGE, T4b). */
  onOpenExifTool: () => void;
  /** Ouvre l'outil « Publier des tags vers EXIF » (PAGE, split v0.53.0). */
  onOpenExifPublish: () => void;
  /** Ouvre « Corriger les extensions » (MODALE, correction des fichiers mal étiquetés). */
  onOpenFixExtensions: () => void;
  /** Ouvre « Nettoyer les dossiers vides » (MODALE, suppression des répertoires sans fichier). */
  onOpenCleanEmptyFolders: () => void;
  /** Ouvre la planche de revue du dédoublonnage (PAGE, T6b). */
  onOpenDedup: () => void;
  /** Ouvre la gestion de la taxonomie (PAGE, T5 lot B). */
  onOpenTaxonomy: () => void;
  /** Ouvre la modale de TÉLÉVERSEMENT de fichiers (XPHOTO-16). */
  onOpenUpload: () => void;
  /**
   * L'utilisateur a-t-il l'accès ÉCRITURE à la bibliothèque (propriétaire/superadmin) ?
   * L'entrée « Importer des fichiers » n'est proposée que dans ce cas (un grantee
   * lecture ne peut pas téléverser → 403).
   */
  canUpload: boolean;
  /**
   * L'utilisateur a-t-il l'accès ÉCRITURE à la bibliothèque (superadmin/admin/owner,
   * cf. `useCanWrite`) ? Un grantee LECTURE SEULE (rôle `user`, `libraryId` null) reçoit
   * un 403 sur toute mutation : on MASQUE donc pour lui le menu OUTILS (toutes ses entrées
   * sont des écritures/admin), les DEUX boutons directs de la barre de sélection
   * (« Déplacer… » et « Supprimer la sélection ») et, dans « Actions… », les entrées
   * d'écriture (taguer, IA, pivoter, compresser…). Ne restent en lecture que Noter (choix
   * produit collaboratif), Diaporama et Télécharger. En pratique `canWrite === canUpload`.
   */
  canWrite: boolean;
  /**
   * Lance le TAGGING IA (T5 lot C) : propose des tags par le LLM pour toute la
   * bibliothèque (ou la sélection courante). Retourne `'ok'` / `'no-provider'`
   * (409 sans fournisseur) / `'error'` → le menu affiche le bon message.
   */
  onTagAi: () => Promise<'ok' | 'no-provider' | 'disabled' | 'error'>;
  /**
   * Coupe-circuit IA global (Incrément B). Quand `false`, l'entrée « Tagger la
   * sélection avec l'IA » du menu « Actions… » est MASQUÉE pour tous les rôles.
   * Défaut `true` (comportement actuel / défaut sûr si le contexte n'est pas monté).
   */
  aiEnabled?: boolean;
}

const DENSITY_KEYS: Record<Density, 'density.S' | 'density.M' | 'density.L'> = {
  S: 'density.S',
  M: 'density.M',
  L: 'density.L',
};

/**
 * Durée du garde-fou optimiste couvrant la fenêtre entre l'enfilage du scan et son
 * apparition dans l'activité des jobs (SEED/SSE). Assez long pour couvrir la latence,
 * assez court pour ne jamais bloquer si l'activité ne remonte finalement rien.
 */
const SCAN_APPEAR_GRACE_MS = 8000;

export function Toolbar({
  total,
  selectedCount,
  shareTarget,
  shareTitle,
  onClearSelection,
  selectionMode,
  onToggleSelectionMode,
  selectionAvailable,
  onSelectAll,
  selectAllBusy,
  selectAllError,
  sort,
  onSortChange,
  dir,
  onDirChange,
  onReshuffle,
  reshuffleBusy,
  reshuffleError,
  mediaType,
  onMediaTypeChange,
  imageCount,
  animatedImageCount,
  videoCount,
  untaggedOnly,
  onUntaggedOnlyChange,
  aiReviewOnly,
  onAiReviewOnlyChange,
  savedFilters,
  savedFiltersLoading,
  savedFiltersError,
  activeExpr,
  onApplySavedFilter,
  onSaveCurrentFilter,
  onEditExpr,
  onManageFilters,
  onOpenAdvancedSearch,
  advancedActive,
  density,
  onDensityChange,
  withAudio,
  onMoveWithAudio,
  onMoveSelection,
  onCompressSelection,
  onRotateSelection,
  onRateSelection,
  onAddTagSelection,
  onTagSelection,
  onSlideshowSelection,
  onAcceptAiTags,
  onDeleteSelection,
  onDownloadSelection,
  downloadBusy,
  downloadError,
  onScanQueued,
  scanActive,
  onOpenExifTool,
  onOpenExifPublish,
  onOpenFixExtensions,
  onOpenCleanEmptyFolders,
  onOpenDedup,
  onOpenTaxonomy,
  onOpenUpload,
  canUpload,
  canWrite,
  onTagAi,
  aiEnabled = true,
}: ToolbarProps): React.JSX.Element {
  const { t, locale } = useI18n();
  // `running` = requête d'enfilage EN VOL ; `queued` = enfilée, en attente d'apparaître
  // dans l'activité (garde-fou optimiste) ; `error` = échec d'enfilage.
  // `busy` = 409 JOB_ALREADY_RUNNING (audit A13) : une analyse est déjà en file/en cours.
  // État DISTINCT de `error` — ce n'est pas une panne, c'est un refus explicable.
  const [scanState, setScanState] = useState<'idle' | 'running' | 'queued' | 'busy' | 'error'>(
    'idle',
  );
  // Tagging IA : `running` (enfilage en vol) et message d'issue (message d'erreur ou
  // « pas de fournisseur » affiché sous les actions du menu Outils).
  const [aiRunning, setAiRunning] = useState(false);
  const [aiMessage, setAiMessage] = useState<string | undefined>(undefined);

  // Un scan est « en cours » si l'enfilage est EN VOL ou vient d'être enfilé (optimisme
  // immédiat, couvre la fenêtre avant l'apparition du job) OU si l'activité des jobs
  // signale un job `scan` ACTIF (réconciliation avec l'état réel — verrou principal).
  const scanRunning = scanState === 'running' || scanState === 'queued' || scanActive;

  const densityOptions: MenuOption<Density>[] = DENSITY_ORDER.map((d) => ({
    value: d,
    label: t(DENSITY_KEYS[d]),
  }));

  const runScan = async (): Promise<void> => {
    // Anti double-lancement : rien si la lib manque, si la requête est déjà en vol,
    // ou si un scan est déjà actif dans l'activité des jobs.
    if (!LIBRARY_ID || scanRunning) return;
    setScanState('running');
    try {
      await enqueueScan(LIBRARY_ID, false);
      recordAction('scan.enqueue');
      // Le job est enfilé mais n'apparaît pas forcément ENCORE dans l'activité
      // (latence SEED/SSE). On passe « queued » : le verrou local reste posé le temps
      // que `scanActive` prenne le relais (garde-fou temporisé dans l'effet ci-dessous).
      setScanState('queued');
      onScanQueued();
    } catch (err) {
      // 409 `JOB_ALREADY_RUNNING` : un scan est déjà enfilé (autre onglet, autre session,
      // téléversement en cours). Message dédié plutôt qu'un « échec » trompeur.
      const alreadyRunning =
        err instanceof ApiError && err.status === 409 && err.code === 'JOB_ALREADY_RUNNING';
      setScanState(alreadyRunning ? 'busy' : 'error');
    }
  };

  // Garde-fou de la fenêtre enfilage→apparition : après un enfil réussi (`queued`),
  // on maintient le verrou local un court instant pour couvrir la latence avant que
  // le job `scan` ne remonte dans l'activité. Si `scanActive` confirme entre-temps,
  // le verrou réel prend le relais ; sinon on relâche après le délai (évite tout blocage).
  useEffect(() => {
    if (scanState !== 'queued') return;
    if (scanActive) {
      setScanState('idle');
      return;
    }
    const id = setTimeout(() => setScanState('idle'), SCAN_APPEAR_GRACE_MS);
    return () => clearTimeout(id);
  }, [scanState, scanActive]);

  const runTagAi = async (): Promise<void> => {
    if (!LIBRARY_ID || aiRunning) return;
    setAiMessage(undefined);
    setAiRunning(true);
    try {
      const outcome = await onTagAi();
      if (outcome === 'no-provider') setAiMessage(t('tools.tagAiNoProvider'));
      else if (outcome === 'disabled') setAiMessage(t('tools.tagAiDisabled'));
      else if (outcome === 'error') setAiMessage(t('tools.tagAiFailed'));
    } finally {
      setAiRunning(false);
    }
  };

  const toolActions: ToolAction[] = [
    // Importer des fichiers (XPHOTO-16) — réservé à l'accès écriture (owner/superadmin) ;
    // en tête car c'est une action d'ajout de premier plan. Présent aussi dans le menu
    // de débordement MOBILE (mêmes `toolActions`).
    ...(canUpload
      ? [{ id: 'upload', label: t('tools.upload'), onSelect: onOpenUpload, disabled: !LIBRARY_ID }]
      : []),
    {
      id: 'scan',
      label: scanRunning ? t('tools.scanning') : t('tools.scan'),
      onSelect: runScan,
      disabled: scanRunning || !LIBRARY_ID,
    },
    // Le tagging IA est dans le menu « Actions… » d'une sélection (`selectionActions`) ;
    // le Journal des appels IA est une section de la console d'admin (superadmin).
    { id: 'taxonomy', label: t('tools.taxonomy'), onSelect: onOpenTaxonomy, disabled: !LIBRARY_ID },
    { id: 'dedup', label: t('tools.dedup'), onSelect: onOpenDedup, disabled: !LIBRARY_ID },
    { id: 'exif', label: t('tools.exif'), onSelect: onOpenExifTool, disabled: !LIBRARY_ID },
    {
      id: 'exifPublish',
      label: t('tools.exifPublish'),
      onSelect: onOpenExifPublish,
      disabled: !LIBRARY_ID,
    },
    // Corriger les extensions (renommage physique) — réservé à l'accès écriture.
    ...(canUpload
      ? [
          {
            id: 'fixExtensions',
            label: t('tools.fixExtensions'),
            onSelect: onOpenFixExtensions,
            disabled: !LIBRARY_ID,
          } satisfies ToolAction,
          // Nettoyer les dossiers vides (suppression de répertoires) — même gate d'écriture.
          {
            id: 'cleanEmptyFolders',
            label: t('tools.cleanEmptyFolders'),
            onSelect: onOpenCleanEmptyFolders,
            disabled: !LIBRARY_ID,
          } satisfies ToolAction,
        ]
      : []),
  ];

  // Actions de sélection regroupées dans un seul menu « Actions… » (barre allégée).
  // Réutilise `ToolsMenu` (mécanique d'accessibilité UNIQUE) : chaque entrée porte son
  // icône et l'état occupé se traduit par un libellé + `disabled` (comme le scan).
  // Actions d'ÉCRITURE de la sélection (taguer, IA, pivoter, compresser) — MASQUÉES pour
  // un lecteur (`!canWrite`), qui reçoit un 403 côté serveur. Ne restent alors que les
  // actions de LECTURE : Noter (choix produit collaboratif), Diaporama et Télécharger.
  //
  // « Déplacer » et « Supprimer » N'Y SONT PLUS (XPHOTO-32 lot 2) : ce sont les deux
  // gestes du RANGEMENT, donc les plus fréquents dès qu'on trie — les laisser au second
  // niveau coûtait un clic d'ouverture de menu à chaque photo. Ils sont promus en boutons
  // DIRECTS à côté du compteur (ci-dessous), avec les MÊMES rappels, et ne sont pas
  // dupliqués ici. La suppression n'a donc plus de séparateur à détacher.
  const selectionActions: ToolAction[] = [
    {
      id: 'rate',
      label: t('bulkRating.rateSelection'),
      icon: <StarIcon className="h-5 w-5" aria-hidden="true" />,
      onSelect: onRateSelection,
    },
    ...(canWrite
      ? [
          {
            id: 'addTag',
            label: t('bulkTag.addTag'),
            icon: <TagIcon className="h-5 w-5" aria-hidden="true" />,
            onSelect: onAddTagSelection,
          } satisfies ToolAction,
          {
            id: 'tag',
            label: t('select.tagSelection'),
            icon: <PencilSquareIcon className="h-5 w-5" aria-hidden="true" />,
            onSelect: onTagSelection,
          } satisfies ToolAction,
          // Entrées IA MASQUÉES quand le coupe-circuit global est désactivé (Incrément B) —
          // pour TOUS les rôles ; le superadmin réactive via « Configuration LLM ».
          ...(aiEnabled
            ? [
                {
                  id: 'tagAi',
                  label: t('bulkTag.tagAiSelection'),
                  icon: <SparklesIcon className="h-5 w-5" aria-hidden="true" />,
                  onSelect: runTagAi,
                  disabled: aiRunning || !LIBRARY_ID,
                } satisfies ToolAction,
                {
                  id: 'acceptAi',
                  label: t('bulkTag.acceptAi'),
                  icon: <CheckBadgeIcon className="h-5 w-5" aria-hidden="true" />,
                  onSelect: onAcceptAiTags,
                } satisfies ToolAction,
              ]
            : []),
          {
            id: 'rotate',
            label: t('rotate.action'),
            icon: <ArrowPathRoundedSquareIcon className="h-5 w-5" aria-hidden="true" />,
            onSelect: onRotateSelection,
          } satisfies ToolAction,
          {
            id: 'compress',
            label: t('compress.action'),
            icon: <ArrowsPointingInIcon className="h-5 w-5" aria-hidden="true" />,
            onSelect: onCompressSelection,
          } satisfies ToolAction,
        ]
      : []),
    {
      id: 'slideshow',
      label: t('select.slideshow'),
      icon: <PlayIcon className="h-5 w-5" aria-hidden="true" />,
      onSelect: onSlideshowSelection,
    },
    {
      id: 'download',
      label: downloadBusy ? t('select.downloading') : t('select.download'),
      icon: downloadBusy ? (
        <ArrowPathIcon
          className="h-5 w-5 animate-spin motion-reduce:animate-none"
          aria-hidden="true"
        />
      ) : (
        <ArrowDownTrayIcon className="h-5 w-5" aria-hidden="true" />
      ),
      onSelect: onDownloadSelection,
      disabled: downloadBusy,
    },
  ];

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-border pb-3">
      {/* Compteur (total via l'API). */}
      <p className="text-sm text-muted" aria-live="polite">
        <span className="font-semibold text-fg">{total.toLocaleString(locale)}</span>{' '}
        {t('gallery.countLabel')}
      </p>

      {/* ENTRÉE DU MODE SÉLECTION (v1.41.0) puis, une fois dedans, « Tout sélectionner (M) »
          / « Tout désélectionner ».

          Le bouton bascule est un VRAI bouton d'état : même nœud, libellé « Sélectionner »
          ↔ « Terminé » et `aria-pressed` qui suit le mode — l'information n'est jamais
          portée par la couleur seule. Il remplace l'entrée de secours d'avant (« Tout
          sélectionner », alors TOUJOURS visible), qui n'a plus de raison d'être hors du
          mode et le rejoint donc.

          Masqué dans la vue « cartes de dossiers » (aucune image) et si le groupe est vide. */}
      {selectionAvailable && total > 0 && (
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onToggleSelectionMode}
            aria-pressed={selectionMode}
            className="flex h-9 items-center gap-1.5 rounded-md px-2.5 text-sm font-medium text-muted transition-colors hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand aria-pressed:bg-surface-2 aria-pressed:text-fg motion-reduce:transition-none"
          >
            {selectionMode ? (
              <CheckIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
            ) : (
              <CursorArrowRaysIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
            )}
            <span>{selectionMode ? t('select.exitMode') : t('select.enterMode')}</span>
          </button>
          {selectionMode &&
            (selectedCount === total ? (
              <button
                type="button"
                onClick={onClearSelection}
                className="flex h-9 items-center gap-1.5 rounded-md px-2.5 text-sm font-medium text-muted transition-colors hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none"
              >
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                <span>{t('select.deselectAll')}</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={onSelectAll}
                disabled={selectAllBusy}
                className="flex h-9 items-center gap-1.5 rounded-md px-2.5 text-sm font-medium text-muted transition-colors hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50 motion-reduce:transition-none"
              >
                <CheckCircleIcon className="h-5 w-5" aria-hidden="true" />
                <span>
                  {selectAllBusy
                    ? t('select.selecting')
                    : interpolate(t('select.selectAll'), { count: total }, locale)}
                </span>
              </button>
            ))}
        </div>
      )}

      {/* BARRE DE SÉLECTION — réservée au MODE (v1.41.0) : compteur, effacement, et les
          actions de masse. Hors mode, plus rien de tout cela n'encombre la barre.

          `selectedCount > 0` implique `selectionMode` (la règle est
          `explicite || sélection non vide`) : garder cette condition sur les ACTIONS ne
          les fait donc jamais apparaître hors du mode — elle leur évite en plus d'être
          proposées quand le mode vient d'être armé et que rien n'est encore coché
          (une « Corbeille » qui ne porterait sur rien). Le compteur, lui, s'affiche dès
          l'entrée dans le mode : c'est le retour visuel qui dit qu'on y est. */}
      {selectionMode && (
        <>
          <span className="flex items-center gap-1 rounded-md bg-surface-2 px-2 py-1 text-sm text-fg">
            {interpolate(t('select.selectedCount'), { count: selectedCount }, locale)}
            {selectedCount > 0 && (
              <button
                type="button"
                onClick={onClearSelection}
                aria-label={t('select.clear')}
                title={t('select.clear')}
                className={`${ICON_BUTTON_SIZE} rounded-md text-muted hover:bg-surface hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand`}
              >
                <XMarkIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </span>
          {/* Les DEUX gestes du rangement, promus hors du menu (XPHOTO-32 lot 2) :
              « Déplacer… » et « Supprimer la sélection », en boutons directs, avec les
              MÊMES rappels que les anciennes entrées de menu (aucune seconde logique).

              GARDÉS par `canWrite`, comme leur jumeau « Déplacer toutes les vidéos avec
              son » plus bas : ce sont des écritures que le serveur refuse en 403 pour un
              lecteur — et c'est exactement le piège de l'audit A2, qu'un bouton sorti du
              menu échappe au bloc `...(canWrite ? […] : [])` qui gardait l'entrée. */}
          {canWrite && selectedCount > 0 && (
            <>
              <button
                type="button"
                onClick={onMoveSelection}
                className="flex h-9 items-center gap-1.5 rounded-md px-2.5 text-sm font-medium text-muted transition-colors hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none"
              >
                <FolderArrowDownIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                <span>{t('organize.moveSelection')}</span>
              </button>
              {/* Suppression = soft-delete RÉVERSIBLE vers la corbeille (une confirmation
                  la rappelle). Sens porté par l'icône poubelle ET le libellé, jamais par
                  la couleur seule. */}
              <button
                type="button"
                onClick={onDeleteSelection}
                className="flex h-9 items-center gap-1.5 rounded-md px-2.5 text-sm font-medium text-danger transition-colors hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none"
              >
                <TrashIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                <span>{t('trash.deleteSelection')}</span>
              </button>
            </>
          )}
          {/* Menu « Actions… » regroupant le RESTE des actions de sélection (Noter,
              Ajouter un tag, Tagger IA, Diaporama, Télécharger…). Allège la barre et
              collapse naturellement en mobile. La mécanique d'accessibilité est celle
              de `ToolsMenu` (aucune duplication). */}
          {selectedCount > 0 && (
            <ToolsMenu
              label={t('select.actions')}
              closeLabel={t('a11y.closeMenu')}
              triggerVariant="labelled"
              trigger={
                <>
                  <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />
                  <span>{t('select.actions')}</span>
                </>
              }
              actions={selectionActions}
            />
          )}
        </>
      )}

      <div className="ms-auto flex flex-wrap items-center gap-2">
        {/* Déplacer toutes les vidéos avec son (LOT 2) : visible quand le filtre
            « Vidéos avec son » (menu Trier & filtrer) est actif et qu'il en existe.
            Résolution serveur (hasAudio).

            GARDÉ par `canWrite` (audit A2) : ce bouton vit HORS du menu « Actions… » et
            avait donc échappé au bloc `...(canWrite ? […] : [])` qui garde son jumeau
            `id: 'move'`. Un lecteur qui activait le filtre « Vidéos avec son » se voyait
            proposer un déplacement de masse que le serveur refuse en 403. */}
        {canWrite && mediaType === 'videoWithSound' && withAudio > 0 && (
          <button
            type="button"
            onClick={onMoveWithAudio}
            className="flex h-11 items-center gap-1.5 rounded-md px-3 text-sm font-medium text-muted transition-colors hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none"
          >
            <FolderArrowDownIcon className="h-5 w-5" aria-hidden="true" />
            <span>{t('organize.moveWithAudio')}</span>
          </button>
        )}

        {/* Menu « Mes filtres » (filtres enregistrés + expression booléenne) — à côté de
            « Trier & filtrer ». Appliquer un filtre / enregistrer l'actuel / éditer
            l'expression / gérer la liste. */}
        <SavedFiltersMenu
          filters={savedFilters}
          loading={savedFiltersLoading}
          errorMessage={savedFiltersError}
          activeExpr={activeExpr}
          onApply={onApplySavedFilter}
          onSaveCurrent={onSaveCurrentFilter}
          onEditExpr={onEditExpr}
          onManage={onManageFilters}
        />

        {/* Menu FILTRER (façon Windows) : critère + ordre + filtre Type dans un
            seul menu (remplace l'ancien menu de tri + le bouton bascule de sens). */}
        <SortMenu
          sort={sort}
          onSortChange={onSortChange}
          dir={dir}
          onDirChange={onDirChange}
          onReshuffle={onReshuffle}
          reshuffleBusy={reshuffleBusy}
          mediaType={mediaType}
          onMediaTypeChange={onMediaTypeChange}
          imageCount={imageCount}
          animatedImageCount={animatedImageCount}
          videoCount={videoCount}
          videoWithSoundCount={withAudio}
          untaggedOnly={untaggedOnly}
          onUntaggedOnlyChange={onUntaggedOnlyChange}
          aiReviewOnly={aiReviewOnly}
          onAiReviewOnlyChange={onAiReviewOnlyChange}
          aiEnabled={aiEnabled}
        />

        {/* Partager — crée un LIEN PUBLIC du dossier courant ou de la sélection.
            Volontairement HORS des menus « Outils » et « Actions… », gardés par
            `canWrite` : partager ce qu'on a le droit de voir est une action de LECTURE,
            et l'API applique la même règle. */}
        <ShareButton label={t('share.view')} target={shareTarget} defaultTitle={shareTitle} />

        {/* Recherche : UNIQUE point d'entrée (nom de fichier + note + type + tags), ouvre
            le panneau `AdvancedSearchModal`. Le surlignage `advancedActive` n'est qu'un
            RAPPEL visuel : les critères actifs sont affichés en toutes lettres par les
            puces (jamais la couleur seule pour l'information). Icône toujours visible ;
            libellé « Recherche » dès `sm`. */}
        <button
          type="button"
          onClick={onOpenAdvancedSearch}
          aria-label={t('advancedSearch.open')}
          title={t('advancedSearch.open')}
          className={`flex h-11 items-center gap-1.5 rounded-md border px-3 text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none ${
            advancedActive
              ? 'border-brand bg-brand/10 text-brand'
              : 'border-border text-fg hover:bg-surface-2'
          }`}
        >
          <MagnifyingGlassIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
          <span className="hidden sm:inline">{t('advancedSearch.open')}</span>
        </button>

        {/* Densité (S/M/L). */}
        <Menu
          label={t('density.open')}
          closeLabel={t('a11y.closeMenu')}
          trigger={<Squares2X2Icon className="h-6 w-6" aria-hidden="true" />}
          options={densityOptions}
          value={density}
          onSelect={onDensityChange}
        />

        {/* Outils (jobs de masse) — MASQUÉ en mobile (allègement de l'en-tête) ;
            réapparaît dès `sm` (≥ 640px). Toutes les entrées de `toolActions` sont des
            écritures/admin (scan, taxonomie, dédup, EXIF, upload…) → le bloc entier est
            MASQUÉ pour un lecteur (`!canWrite`) : jamais de menu Outils vide. */}
        {canWrite && (
          <div className="hidden sm:block">
            <ToolsMenu
              label={t('tools.open')}
              closeLabel={t('a11y.closeMenu')}
              trigger={<WrenchScrewdriverIcon className="h-6 w-6" aria-hidden="true" />}
              actions={toolActions}
              error={
                scanState === 'error'
                  ? t('tools.scanFailed')
                  : scanState === 'busy'
                    ? t('tools.scanAlreadyRunning')
                    : undefined
              }
            />
          </div>
        )}

        {/* Menu de DÉBORDEMENT MOBILE (suivi planifié de v0.57.0) — visible SEULEMENT
            en mobile (`sm:hidden` porté par le composant) : expose toutes les entrées du
            menu Outils (mêmes actions, réutilisées). Disparaît dès `sm` où le menu Outils
            réapparaît. Comme le menu Outils, MASQUÉ pour un lecteur (aucune entrée à offrir). */}
        {canWrite && (
          <OverflowMenu
            label={t('tools.openOverflow')}
            closeLabel={t('a11y.closeMenu')}
            trigger={<Bars3Icon className="h-6 w-6" aria-hidden="true" />}
            actions={toolActions}
          />
        )}
      </div>

      {/* Erreur du « Tout sélectionner » (403 grantee, réseau) — alerte inline non
          bloquante, texte + icône (jamais la couleur seule). */}
      {reshuffleError && (
        <div
          role="alert"
          className="flex w-full items-start gap-2 rounded-md border border-danger bg-danger/10 px-3 py-2 text-sm text-fg"
        >
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-danger" aria-hidden="true" />
          <span className="flex-1">{reshuffleError}</span>
        </div>
      )}

      {selectAllError && (
        <div
          role="alert"
          className="flex w-full items-start gap-2 rounded-md border border-danger bg-danger/10 px-3 py-2 text-sm text-fg"
        >
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-danger" aria-hidden="true" />
          <span className="flex-1">{selectAllError}</span>
        </div>
      )}

      {/* Erreur du téléchargement de la sélection (403 grantee, réseau) — alerte inline
          non bloquante, texte + icône (jamais la couleur seule). */}
      {downloadError && (
        <div
          role="alert"
          className="flex w-full items-start gap-2 rounded-md border border-danger bg-danger/10 px-3 py-2 text-sm text-fg"
        >
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-danger" aria-hidden="true" />
          <span className="flex-1">{downloadError}</span>
        </div>
      )}

      {/* Issue du tagging IA (ex. « pas de fournisseur configuré ») — affichée en
          alerte inline PERSISTANTE (le menu Outils se ferme au clic) ; effaçable.
          Texte + icône, jamais la couleur seule. */}
      {aiMessage && (
        <div
          role="alert"
          className="flex w-full items-start gap-2 rounded-md border border-warning bg-warning/10 px-3 py-2 text-sm text-fg"
        >
          <ExclamationTriangleIcon
            className="h-5 w-5 shrink-0 text-warning-fg"
            aria-hidden="true"
          />
          <span className="flex-1">{aiMessage}</span>
          <button
            type="button"
            onClick={() => setAiMessage(undefined)}
            aria-label={t('gallery.clearFilter')}
            title={t('gallery.clearFilter')}
            className={`${ICON_BUTTON_SIZE} rounded-md text-muted hover:bg-surface hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand`}
          >
            <XMarkIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
}
